import Cookies from "js-cookie";
import store from "./store";
import {
  clearRefreshToken,
  readRefreshToken,
  requestRefresh,
  saveRefreshToken,
} from "./refreshSession";

const STORAGE_KEY = "siraj-accounts";
export const MAX_SAVED_ACCOUNTS = 10;

const USER_SCOPED_KEYS = [
  "userInfo",
  "permissions",
  "subscription",
  "subscriptionData",
  "contact-dashboard-recent-searches",
];

let switching = false;
let recovering = false;

function readTokenPayload(token) {
  if (!token || typeof token !== "string") return null;
  try {
    const payload = token.split(".")[1].replace(/-/g, "+").replace(/_/g, "/");
    const padded = payload + "=".repeat((4 - (payload.length % 4)) % 4);
    return JSON.parse(atob(padded));
  } catch (error) {
    return null;
  }
}

function userIdFromToken(token) {
  const data = readTokenPayload(token);
  if (data?.sub == null) return null;
  return String(data.sub);
}

function accessTokenStillValid(token) {
  const exp = readTokenPayload(token)?.exp;
  if (!exp) return false;
  return exp * 1000 - Date.now() > 60 * 1000;
}

export function activeUserId() {
  return userIdFromToken(Cookies.get("token-siraj"));
}

function slimRole(role) {
  if (!role) return undefined;
  if (typeof role === "string") return { name: role };
  return { id: role.id, name: role.name };
}

function slimSubscription(user) {
  const subscription = user?.subscription;
  if (!subscription || typeof subscription !== "object") return undefined;
  return {
    id: subscription.id,
    name: subscription.name,
    image: subscription.image,
    subDomain: subscription.subDomain,
  };
}

function currentPrefs() {
  return {
    lang: localStorage.getItem("lang") || "",
    langfile: localStorage.getItem("langfile") || "",
    rtl: localStorage.getItem("rtl") === "true",
    timeZone: localStorage.getItem("timeZone") || "",
  };
}

export function readSavedAccounts() {
  try {
    const parsed = JSON.parse(localStorage.getItem(STORAGE_KEY) || "[]");
    if (!Array.isArray(parsed)) return [];
    return parsed.filter((account) => account && account.id && account.refreshToken);
  } catch (error) {
    return [];
  }
}

function writeSavedAccounts(accounts) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(accounts));
}

function upsertAccount(entry) {
  if (!entry?.id || !entry.refreshToken || !entry.accessToken) return;
  const id = String(entry.id);
  const list = readSavedAccounts().filter((account) => account.id !== id);
  list.unshift({
    ...entry,
    id,
    savedAt: Date.now(),
  });
  writeSavedAccounts(list.slice(0, MAX_SAVED_ACCOUNTS));
}

export function removeSavedAccount(userId) {
  if (userId == null) return;
  const id = String(userId);
  writeSavedAccounts(readSavedAccounts().filter((account) => account.id !== id));
}

export function accountTitle(account) {
  const name = [account?.name, account?.lastName].filter(Boolean).join(" ").trim();
  return name || account?.username || "";
}

export function accountPictureSrc(picture, assetBase) {
  if (!picture) return "";
  if (/^https?:\/\//i.test(picture) || picture.startsWith("data:")) return picture;
  return `${assetBase || ""}${picture}`;
}

function displayFromUser(user, existing) {
  return {
    name: user?.name || existing?.name || "",
    lastName: user?.lastName || existing?.lastName || "",
    username: user?.username || existing?.username || "",
    picture: user?.picture || existing?.picture || "",
    role: slimRole(user?.role) || existing?.role,
    limited: user?.limited ?? existing?.limited,
    subscription: slimSubscription(user) || existing?.subscription,
  };
}

export function captureCurrentSession() {
  const accessToken = Cookies.get("token-siraj");
  const refreshToken = readRefreshToken();
  const id = userIdFromToken(accessToken);
  if (!accessToken || !refreshToken || !id) return;
  const user = store.tokenObject.value?.userState;
  const existing = readSavedAccounts().find((account) => account.id === id);
  upsertAccount({
    id,
    ...displayFromUser(user, existing),
    accessToken,
    refreshToken,
    ...(existing
      ? {
          lang: currentPrefs().lang || existing.lang,
          langfile: currentPrefs().langfile || existing.langfile,
          rtl: currentPrefs().rtl,
          timeZone: currentPrefs().timeZone || existing.timeZone,
        }
      : currentPrefs()),
  });
}

export function ensureActiveAccountListed() {
  captureCurrentSession();
}

export function commitLoginAccount({ accessToken, refreshToken, user }) {
  const id = String(user?.id || userIdFromToken(accessToken) || "");
  if (!id || !accessToken || !refreshToken) return;
  const existing = readSavedAccounts().find((account) => account.id === id);
  upsertAccount({
    id,
    ...displayFromUser(user, existing),
    accessToken,
    refreshToken,
    ...currentPrefs(),
  });
}

export function rememberRefreshedSession(data) {
  const accessToken = data?.accessToken;
  const refreshToken = data?.refreshToken;
  const id = userIdFromToken(accessToken) || userIdFromToken(refreshToken);
  if (!id || !accessToken || !refreshToken) return;
  const existing = readSavedAccounts().find((account) => account.id === id);
  const prefs = currentPrefs();
  upsertAccount({
    id,
    ...displayFromUser(data?.user, existing),
    role: existing?.role || slimRole(data?.user?.role),
    accessToken,
    refreshToken,
    lang: prefs.lang || existing?.lang || "",
    langfile: prefs.langfile || existing?.langfile || "",
    rtl: prefs.rtl,
    timeZone: prefs.timeZone || existing?.timeZone || "",
  });
}

export function syncSavedAccountProfile(user) {
  if (!user?.id) return;
  const id = String(user.id);
  const existing = readSavedAccounts().find((account) => account.id === id);
  if (!existing) return;
  upsertAccount({
    ...existing,
    ...displayFromUser(user, existing),
    ...currentPrefs(),
  });
}

export function clearUserScopedStorage() {
  USER_SCOPED_KEYS.forEach((key) => localStorage.removeItem(key));
}

function applyAccountPrefs(account) {
  if (account.lang) localStorage.setItem("lang", account.lang);
  if (account.langfile) localStorage.setItem("langfile", account.langfile);
  localStorage.setItem("rtl", account.rtl ? "true" : "false");
  if (account.timeZone) localStorage.setItem("timeZone", account.timeZone);
}

function sessionUser(saved, refreshedUser) {
  const merged = {
    id: refreshedUser?.id ?? saved.id,
    name: refreshedUser?.name || saved.name || "",
    lastName: refreshedUser?.lastName || saved.lastName || "",
    username: refreshedUser?.username || saved.username || "",
    picture: refreshedUser?.picture || saved.picture || "",
    limited: refreshedUser?.limited ?? saved.limited,
    ...(refreshedUser || {}),
  };
  delete merged.password;
  const role = slimRole(saved.role) || slimRole(refreshedUser?.role);
  if (role) merged.role = role;
  if (!merged.subscription && saved.subscription) {
    merged.subscription = saved.subscription;
  }
  if (merged.id != null && !Number.isNaN(Number(merged.id))) {
    merged.id = Number(merged.id);
  }
  return merged;
}

function activateSavedAccount(saved, accessToken, refreshToken, refreshedUser) {
  const user = sessionUser(saved, refreshedUser);
  const updated = {
    ...saved,
    ...displayFromUser(user, saved),
    id: String(user.id || saved.id),
    accessToken,
    refreshToken,
    lang: saved.lang || "",
    langfile: saved.langfile || "",
    rtl: Boolean(saved.rtl),
    timeZone: saved.timeZone || "",
  };
  upsertAccount(updated);
  applyAccountPrefs(updated);
  clearUserScopedStorage();
  writeActiveSession({ accessToken, refreshToken, user });
  window.location.replace("/");
}

function writeActiveSession({ accessToken, refreshToken, user }) {
  saveRefreshToken(refreshToken);
  store.tokenObject.set({
    auth: { token: accessToken, type: "Bearer" },
    userState: user || null,
  });
}

function clearActiveSession() {
  clearRefreshToken();
  store.tokenObject.set({ auth: null });
  clearUserScopedStorage();
}

export async function switchToAccount(account, { snapshotCurrent = true } = {}) {
  if (!account?.refreshToken) {
    removeSavedAccount(account?.id);
    const error = new Error("Missing refresh token");
    error.response = { status: 401 };
    throw error;
  }
  if (switching) return;
  switching = true;
  try {
    if (snapshotCurrent) captureCurrentSession();
    const saved =
      readSavedAccounts().find((item) => item.id === String(account.id)) || account;
    if (accessTokenStillValid(saved.accessToken)) {
      activateSavedAccount(
        saved,
        saved.accessToken,
        saved.refreshToken,
        null
      );
      return;
    }
    let data;
    try {
      data = await requestRefresh(saved.refreshToken);
    } catch (error) {
      const status = error?.response?.status;
      if (status === 401 || status === 403) removeSavedAccount(saved.id);
      throw error;
    }
    if (!data?.accessToken || !data?.refreshToken) {
      removeSavedAccount(saved.id);
      const error = new Error("Refresh failed");
      error.response = { status: 401 };
      throw error;
    }
    activateSavedAccount(
      data.user ? { ...saved, ...displayFromUser(data.user, saved) } : saved,
      data.accessToken,
      data.refreshToken,
      data.user
    );
  } catch (error) {
    switching = false;
    throw error;
  }
}

export async function signOutActiveAccount({
  activateNext = true,
  redirect = true,
} = {}) {
  const currentId = activeUserId();
  if (currentId) removeSavedAccount(currentId);
  if (activateNext) {
    const rest = readSavedAccounts();
    for (const account of rest) {
      try {
        await switchToAccount(account, { snapshotCurrent: false });
        return;
      } catch (error) {
        removeSavedAccount(account.id);
      }
    }
  }
  clearActiveSession();
  if (redirect) window.location.replace("/login");
}

export async function recoverAfterRefreshFailure() {
  if (recovering) return;
  recovering = true;
  try {
    const failedId = activeUserId();
    if (failedId) removeSavedAccount(failedId);
    const rest = readSavedAccounts();
    for (const account of rest) {
      try {
        await switchToAccount(account, { snapshotCurrent: false });
        return;
      } catch (error) {
        removeSavedAccount(account.id);
      }
    }
    clearActiveSession();
    window.location.replace("/login");
  } finally {
    recovering = false;
  }
}
